import React from 'react';
import PropTypes from 'prop-types';

import Style from './datesGeneratorForm.module.css';
import { useTranslation } from 'react-i18next';
import { Form, Button, Date, Section, TimeInput, Icon, Select, TooltipHelp, CheckboxLabel } from '@weezevent/nacre';
import i18n from 'i18next';
import moment from 'moment';
import { daysForLocale } from './../../utils';

function Slot({ id, error = false, value = null, onChange = () => {}, onDelete = () => {}, slots = [] }) {
    const { t } = useTranslation();

    return (
        <div className={Style['slot']}>
            <Form.Field className={Style['slot-field']}>
                <Form.Label>{t('ticket.sessions.create.dates.startHour')}</Form.Label>
                <TimeInput errors={error} className={Style['slot-input']} step={15} key={`time-${id}`} onChange={time => onChange({ time, id })} value={value} />
            </Form.Field>
            {slots.length > 1 && <Icon name="cross" onClick={() => onDelete(id)} />}
        </div>
    );
}

Slot.propTypes = {
    id: PropTypes.number.isRequired,
    value: PropTypes.string,
    onChange: PropTypes.func,
    onDelete: PropTypes.func,
    slots: PropTypes.array
};

const nextSlot = lastTime => {
    let currentHour = Number(lastTime.split(':')[0]);
    if (currentHour === 23) {
        return '23:59';
    }

    return `${String(currentHour + 1).padStart(2, '0')}:00`;
};

const sortSlot = slots => {
    return slots.sort((slot_1, slot_2) => slot_1.start.localeCompare(slot_2.start));
};

const checkDuplicatedSlots = slots => {
    let duplicatedSlotIds = [];
    slots.forEach(slot => {
        let duplicateSlot = slots.filter(s => s.start === slot.start && s.id !== slot.id);
        if (duplicateSlot.length > 0) {
            duplicatedSlotIds = [...duplicatedSlotIds, ...duplicateSlot.map(s => s.id)];
        }
    });

    return Array.from(new Set(duplicatedSlotIds));
};

function ManagerSlots({ errors = {}, errorsSlot = [], onChangeErrorsSlot = () => {}, form = {}, onChangeForm = () => {} }) {
    const { t } = useTranslation();

    const addSlot = React.useCallback(() => {
        let ids = form.slots.reduce((acc, slot) => {
            acc.push(slot.id);

            return acc;
        }, []);
        let newId = Math.max(...ids) + 1;

        let newSlots = [...form.slots];
        let _nextSlot = nextSlot(newSlots[newSlots.length - 1].start);
        newSlots.push({ id: newId, start: _nextSlot });

        onChangeErrorsSlot(checkDuplicatedSlots(newSlots));

        onChangeForm('slots', newSlots);
    }, [form.slots, onChangeForm, onChangeErrorsSlot]);

    const onDelete = React.useCallback(
        id => {
            let newSlots = [...form.slots].filter(slot => slot.id !== id);

            onChangeErrorsSlot(checkDuplicatedSlots(newSlots));

            onChangeForm('slots', newSlots);
        },
        [onChangeForm, form.slots, onChangeErrorsSlot]
    );

    const onChange = React.useCallback(
        ({ time, id }) => {
            let newSlots = [...form.slots].filter(slot => slot.id !== id);
            newSlots.push({ id, start: time });
            newSlots = sortSlot(newSlots);

            onChangeErrorsSlot(checkDuplicatedSlots(newSlots));

            onChangeForm('slots', newSlots);
        },
        [onChangeForm, form.slots, onChangeErrorsSlot]
    );

    return (
        <div className={Style['manager_slots']}>
            <Section>
                {form.slots.map(({ id, start }) => (
                    <Slot key={id} error={errorsSlot.includes(id)} id={id} value={start} onChange={onChange} onDelete={onDelete} slots={form.slots} />
                ))}
                {Boolean(errorsSlot.length) && <Form.FieldErrors className={Style['errors-slot']} errors={[t('ticket.sessions.create.dates.error.slots')]} />}
                <Button primary inverted onClick={addSlot}>
                    {t('ticket.sessions.create.dates.addSlot')}
                </Button>
            </Section>
        </div>
    );
}

function DaysOfWeek({ formDays = [], onChangeForm = () => {}, locale = 'en-GB', errors = {} }) {
    const { t } = useTranslation();
    const days = daysForLocale(locale);

    const onChange = React.useCallback(
        (e, { value, checked }) => {
            let newDays = new Set(formDays);
            if (!checked) {
                newDays.delete(Number(value));
            } else {
                newDays.add(Number(value));
            }
            onChangeForm('days_of_week', Array.from(newDays).sort());
        },
        [onChangeForm]
    );

    return (
        <Form.Field className={Style['days-week']}>
            <Form.Label>{t('ticket.sessions.create.dates.daysWeek')}</Form.Label>
            <div className={Style['days-week-container-checkboxes']}>
                {days.map((day, index) => (
                    <CheckboxLabel
                        key={index + 1}
                        value={index + 1}
                        errors={Boolean(errors['days_of_week'])}
                        onChange={onChange}
                        checked={formDays.includes(index + 1)}
                        label={day}
                        name={day}
                    />
                ))}
            </div>
            <Form.FieldErrors className={Style['days-week-field-error']} errors={[errors['days_of_week'] ? t(errors['days_of_week']) : null]} />
        </Form.Field>
    );
}

function RepeatConfig({ errors, form, onChangeForm, onChangeFormObject, onChangeDate, timezone, locale, currentYear }) {
    const { t } = useTranslation();

    const repeatModeOptions = [
        {
            key: 0,
            text: t('ticket.sessions.create.dates.repeat.noRepeat'),
            value: null
        },
        {
            key: 1,
            text: t('ticket.sessions.create.dates.repeat.daily'),
            value: 'daily'
        },
        {
            key: 2,
            text: t('ticket.sessions.create.dates.repeat.weekly'),
            value: 'weekly'
        }
    ];

    const onChange = React.useCallback(
        value => {
            if (!value) {
                onChangeFormObject({ repeat_mode: null, days_of_week: [], end_date: null }, true);
            }

            let endDateFormatted = form.end_date;
            if (!endDateFormatted) {
                const _endDate = moment(form.start_date).add(1, 'days');
                // datetime.fromisoformat in Pyvar, causes an error if a date string is in UTC with this
                // format YYYY-MM-DDTHH:mm:ssZ. For this case precisely we format like this YYYY-MM-DDTHH:mm:ss.
                // If the date string has a utcOffset !== 0 , this format YYYY-MM-DDTHH:mm:ss+HH:mm:ss works
                endDateFormatted = _endDate.format(_endDate.isUTC() ? 'YYYY-MM-DDTHH:mm:ss' : null);
            }

            if (value === 'daily') {
                onChangeFormObject({ repeat_mode: 'daily', days_of_week: [1, 2, 3, 4, 5, 6, 7], end_date: endDateFormatted }, true);
            }

            if (value === 'weekly') {
                onChangeFormObject({ repeat_mode: 'weekly', days_of_week: [1, 2, 3, 4, 5, 6, 7], end_date: endDateFormatted }, true);
            }
        },
        [onChangeFormObject, form]
    );

    return (
        <>
            <Form.Field className={form.repeat_mode ? Style['session-form-field'] : Style['session-form-field-wo-margin']}>
                <Form.Label optionalText={t('ticket.common.labelOptional')}>
                    {t('ticket.sessions.create.dates.repeat.label')}
                    <TooltipHelp title={t('ticket.sessions.create.dates.repeat.tooltip')} />
                </Form.Label>
                <Select
                    disabled={!form.start_date}
                    placeholder={t('ticket.common.action.select')}
                    value={form.repeat_mode}
                    onChange={value => {
                        onChange(value);
                    }}
                    options={repeatModeOptions}
                />
            </Form.Field>
            {form.repeat_mode && (
                <Section className={Style['repeat-config-section']}>
                    {form.repeat_mode === 'weekly' && <DaysOfWeek formDays={form.days_of_week} onChangeForm={onChangeForm} locale={locale} errors={errors} />}
                    <Form.Field error={errors['end_date'] ? true : false} className={Style['enddate-field']}>
                        <Form.Label optionalText={t('ticket.common.labelOptional')}>{t('ticket.sessions.create.dates.endDate')}</Form.Label>
                        <Date.Input
                            errors={Boolean(errors['end_date'])}
                            value={form.end_date}
                            locale={locale}
                            returnFormat={'YYYY-MM-DD'}
                            onChange={date => onChangeDate(date, 'end_date')}
                            minYear={currentYear}
                            maxYear={currentYear + 15}
                            minDate={form.start_date ? moment(form.start_date).add(1, 'days') : moment()}
                        />
                        <Form.FieldErrors errors={[errors['end_date'] ?? null]} />
                    </Form.Field>
                </Section>
            )}
        </>
    );
}

export default function DatesGeneratorForm({
    errors = {},
    errorsSlot = [],
    onChangeErrorsSlot = () => {},
    form = {},
    onChangeForm = () => {},
    onChangeFormObject = () => {},
    timezone = 'Europe/Paris'
}) {
    const { t } = useTranslation();
    const locale = i18n.language;

    const currentYear = new Date().getFullYear();

    const onChangeDate = React.useCallback(
        (date, name) => {
            let startDateFormatted = null;
            if (date) {
                let startDate = moment.tz(`${date} 12:00`, timezone);
                // datetime.fromisoformat in Pyvar, causes an error if a date string is in UTC with this
                // format YYYY-MM-DDTHH:mm:ssZ. For this case precisely we format like this YYYY-MM-DDTHH:mm:ss.
                // If the date string has a utcOffset !== 0 , this format YYYY-MM-DDTHH:mm:ss+HH:mm:ss works
                startDateFormatted = startDate.format(startDate.isUTC() ? 'YYYY-MM-DDTHH:mm:ss' : null);
            }
            onChangeForm(name, startDateFormatted);
        },
        [timezone]
    );

    return (
        <div>
            <Form.Field className={`${Style['session-form-field']} ${Style['startdate-field']}`}>
                <Form.Label>{t('ticket.sessions.create.dates.startDate')}</Form.Label>
                <Date.Input
                    errors={Boolean(errors['start_date'])}
                    value={form.start_date}
                    locale={locale}
                    returnFormat={'YYYY-MM-DD'}
                    onChange={date => onChangeDate(date, 'start_date')}
                    minYear={currentYear}
                    maxYear={currentYear + 15}
                    minDate={moment()}
                />
                <Form.FieldErrors errors={[errors['start_date'] ?? null]} />
            </Form.Field>
            <ManagerSlots errors={errors} errorsSlot={errorsSlot} onChangeErrorsSlot={onChangeErrorsSlot} form={form} onChangeForm={onChangeForm} />
            <RepeatConfig
                form={form}
                onChangeForm={onChangeForm}
                onChangeFormObject={onChangeFormObject}
                onChangeDate={onChangeDate}
                timezone={timezone}
                errors={errors}
                locale={locale}
                currentYear={currentYear}
            />
        </div>
    );
}

DatesGeneratorForm.propTypes = {
    errors: PropTypes.object,
    errorsSlot: PropTypes.array,
    onChangeErrorsSlot: PropTypes.func,
    form: PropTypes.object,
    onChangeForm: PropTypes.func,
    onChangeFormObject: PropTypes.func,
    timezone: PropTypes.string
};
